import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/services/api";
import { Loader2, FileText } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;

const ESTADO_FACTURA = {
  pendiente: "bg-red-100 text-red-700",
  parcial: "bg-amber-100 text-amber-800",
  pagada: "bg-green-100 text-green-800",
};

export default function MayoristaCuenta() {
  const [facturas, setFacturas] = useState(null);
  const [cuenta, setCuenta] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/mayoristas/me/facturas"), api.get("/mayoristas/me/estado-cuenta")])
      .then(([f, c]) => {
        setFacturas(f.data);
        setCuenta(c.data);
      })
      .catch(() => toast.error("Error cargando estado de cuenta"));
  }, []);

  if (!facturas || !cuenta) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mayorista-cuenta-page">
      <h2 className="text-lg font-bold text-[#4A2C0A]">Estado de Cuenta</h2>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border p-3 text-center">
          <p className="text-lg font-bold text-[#4A2C0A]">{fmt(cuenta.total_facturado)}</p>
          <p className="text-[10px] text-gray-500 uppercase">Facturado</p>
        </div>
        <div className="bg-white rounded-2xl border p-3 text-center">
          <p className="text-lg font-bold text-green-700">{fmt(cuenta.total_pagado)}</p>
          <p className="text-[10px] text-gray-500 uppercase">Pagado</p>
        </div>
        <div className="bg-white rounded-2xl border p-3 text-center">
          <p className={`text-lg font-bold ${cuenta.saldo_pendiente > 0 ? "text-red-600" : "text-green-700"}`} data-testid="saldo-pendiente">
            {fmt(cuenta.saldo_pendiente)}
          </p>
          <p className="text-[10px] text-gray-500 uppercase">Saldo</p>
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
        <FileText className="w-4 h-4" /> Mis Facturas
      </h3>

      {facturas.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-8">Aún no tienes facturas</p>
      )}

      {facturas.map((f) => (
        <div key={f.id} className="bg-white rounded-2xl border p-4" data-testid={`factura-card-${f.numero}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="font-bold text-[#4A2C0A]">{f.numero}</p>
            <Badge className={`${ESTADO_FACTURA[f.estado]} border-0 capitalize`}>{f.estado}</Badge>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Pedido {f.pedido_numero} · {new Date(f.fecha_creacion).toLocaleDateString("es-CO")}
          </p>
          <div className="flex justify-between text-sm">
            <span>Total</span>
            <span className="font-semibold">{fmt(f.total)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Saldo pendiente</span>
            <span className={`font-semibold ${f.saldo > 0 ? "text-red-600" : "text-green-700"}`}>{fmt(f.saldo)}</span>
          </div>
          {f.pagos?.length > 0 && (
            <div className="mt-2 border-t pt-2 space-y-1">
              {f.pagos.map((p) => (
                <p key={p.id} className="text-xs text-gray-500">
                  Pago {fmt(p.monto)} · {p.metodo} · {new Date(p.fecha).toLocaleDateString("es-CO")}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
